import { useState, useEffect } from 'react'
import {
	AIIcon,
	AISignificanceIcon,
	AISummaryIcon,
	AlertIcon,
	ArrowUpRight,
	InfoIcon,
	LoadingIcon,
} from '../icons'
import TypographyHeader from '../typography/TypographyHeading'
import { Tooltip } from '../modalsAndOverlays'
import {
	TypographyCaps,
	TypographyLabel,
	TypographyParagraph,
} from '../typography'
import { formatAITimestamp, openInNewTab } from '#app/utils/misc.tsx'
import { Button } from '../buttons'
import Skeleton, {
	SkeletonTheme,
} from '#node_modules/react-loading-skeleton/dist'
import { useTheme } from '#app/utils/providers/themeProvider'

// 1) Types
interface SignificanceObject {
	[key: string]: string | undefined
	cultural_significance?: string
	artist_significance?: string
	collection_significance?: string
	historical_significance?: string
	other_notes?: string
}

interface SimilarNFT {
	name: string
	description?: string
	image_url?: string
}

interface EnrichedData {
	// We’ll keep these for the UI sections:
	collection_summary: string
	key_features: string[]
	collaborations: string[]
	significance: string | SignificanceObject
}

interface MarketplaceData {
	floor_price: number
	image_url: string
	banner_url: string
}

// Our local "Metadata" shape for rendering in the UI:
interface Metadata {
	collection_name: string
	token_id: string
	token_type: string
	enriched_data: EnrichedData
	verification_status: string
	marketplace_data: MarketplaceData
	sources: {
		alchemy: boolean
		opensea: boolean
		web: boolean
	}
	marketplaces: {
		marketplace_name: string
		nft_url: string
	}[]
	similar_nfts?: SimilarNFT[]
	analysis_timestamp: string
}

interface LoreAIProps {
	onMessageReceived: (data: any) => void
	contractHash: string
	tokenId: string
	setSignModalOpen?: (open: boolean) => void
}

const LoreAI: React.FC<LoreAIProps> = ({
	onMessageReceived,
	contractHash,
	tokenId,
	setSignModalOpen,
}) => {
	const [loading, setLoading] = useState(true)
	const [metadata, setMetadata] = useState<Metadata | null>(null)
	const [error, setError] = useState<string | null>(null)

	const [theme] = useTheme()

	useEffect(() => {
		if (!metadata && contractHash && tokenId) {
			fetchData()
		} else if (!contractHash || !tokenId) {
			setError('Missing required query data for the chatbot.')
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [metadata, contractHash, tokenId])

	// 2) Fetch data from your server
	const fetchData = async () => {
		try {
			setLoading(true)
			setError(null)

			const response = await fetch(`${import.meta.env.VITE_LORE_AI}/chatbot`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Access-Control-Allow-Origin': '*',
				},
				body: JSON.stringify({ contractHash, tokenId }),
			})

			if (!response.ok) {
				throw new Error(`Server error: ${response.status}`)
			}

			const raw = await response.json()

			// The top-level data is in `raw.data`
			const mainData = raw.data
			if (!mainData) {
				setError('No `data` field in the chatbot response.')
				setLoading(false)
				return
			}

			// mainData.nft, mainData.collection, mainData.market_data, mainData.similar_nfts, etc.
			const nftData = mainData.nft || {}
			const collectionData = mainData.collection || {}
			const marketData = mainData.market_data || {}
			const significance = nftData.significance || {} // can be a string or object

			// Build your the `Metadata` object
			const transformedMetadata: Metadata = {
				// If NFT has no name, fallback to empty
				collection_name: collectionData.name || nftData.collection_name || '',
				token_id: nftData.token_id || '',
				token_type: nftData.chain || '',
				enriched_data: {
					// We don’t have a separate `summary` in mainData, so fallback to NFT's description or the collection's description
					collection_summary:
						nftData.description || collectionData.description || '',
					key_features: [],
					collaborations: [],
					significance: significance,
				},
				verification_status: 'N/A',
				marketplace_data: {
					// If no floor price is returned, default 0
					floor_price: marketData?.floor_price || 0,
					// If we want to set these from the server
					image_url: nftData.image_url || '',
					banner_url: '', // or some fallback
				},
				sources: {
					alchemy: false,
					opensea: false,
					web: false,
				},
				marketplaces: collectionData.marketplaces,
				similar_nfts: mainData.similar_nfts || [],
				analysis_timestamp: mainData.analysis_timestamp,
			}

			setMetadata(transformedMetadata)
			onMessageReceived(raw)
		} catch (err) {
			console.error('Error fetching AI response:', err)
			setError('Unable to fetch NFT metadata. Please try again later.')
		} finally {
			setLoading(false)
		}
	}

	// Helper to display significance (string or object)
	const renderSignificance = (significance: string | SignificanceObject) => {
		if (typeof significance === 'string') {
			return (
				<div className="flex flex-col gap-y-4 rounded-md border border-textInactiveLight bg-uiTertiaryLight p-4 dark:border-uiSecondaryDark dark:bg-grey950">
					<div className="flex flex-row items-center gap-x-1 text-textPrimaryLight dark:text-textPrimaryDark">
						<AISignificanceIcon />
						<TypographyCaps
							size="small"
							additionalClasses="mt-0.5 text-textPrimaryLight dark:text-textPrimaryDark"
						>
							Significance
						</TypographyCaps>
					</div>
					<TypographyParagraph
						size="medium"
						additionalClasses="dark:text-uiTertiaryLight text-uiTertiaryDark"
					>
						{significance}
					</TypographyParagraph>
				</div>
			)
		}

		const entries = Object.entries(significance).filter(([_, value]) => !!value)
		if (entries.length === 0) {
			return (
				<TypographyParagraph
					size="medium"
					additionalClasses="dark:text-uiTertiaryLight text-uiTertiaryDark"
				>
					No significance data available.
				</TypographyParagraph>
			)
		}

		return (
			<div className="flex flex-col gap-y-6">
				{entries.map(([key, value]) => (
					<div
						key={key}
						className="flex flex-col gap-y-4 rounded-md border border-textInactiveLight bg-uiTertiaryLight p-4 dark:border-uiSecondaryDark dark:bg-grey950"
					>
						<div className="flex flex-row items-center gap-x-1 text-textPrimaryLight dark:text-textPrimaryDark">
							<AISignificanceIcon />
							<TypographyCaps
								size="small"
								additionalClasses="mt-0.5 text-textPrimaryLight dark:text-textPrimaryDark"
							>
								{formatKey(key)}
							</TypographyCaps>
						</div>
						<TypographyParagraph
							size="medium"
							additionalClasses="dark:text-uiTertiaryLight text-uiTertiaryDark"
						>
							{value}
						</TypographyParagraph>
					</div>
				))}
			</div>
		)
	}

	// Convert snake_case to "Snake Case"
	const formatKey = (key: string) => {
		return key
			.split('_')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ')
	}

	// Render Similar NFTs, if any
	const renderSimilarNFTs = (similarNfts: SimilarNFT[] | undefined) => {
		if (!similarNfts || similarNfts.length === 0) return null

		return (
			<div className="mt-8">
				<h3 className="text-gray-900 dark:text-gray-100 mb-3 text-sm font-semibold uppercase tracking-wide">
					Similar NFTs
				</h3>
				<div className="space-y-4">
					{similarNfts.map((nft, index) => (
						<div
							key={index}
							className="border-gray-200 dark:border-gray-700 rounded border p-3"
						>
							<h4 className="text-gray-900 dark:text-gray-100 text-base font-semibold">
								{nft.name}
							</h4>
							{nft.description && (
								<p className="text-gray-700 dark:text-gray-300 mt-1 text-base">
									{nft.description}
								</p>
							)}
							{nft.image_url && (
								<img
									src={nft.image_url}
									alt={nft.name}
									className="mt-2 max-w-full rounded"
								/>
							)}
						</div>
					))}
				</div>
			</div>
		)
	}

	return (
		<div className="w-full text-textSecondaryLight dark:text-textSecondaryDark">
			<div className="mt-6 space-y-6 rounded-lg border border-textInactiveLight bg-uiSecondaryLight p-6 shadow-sm dark:border-uiSecondaryDark dark:bg-[#111111]">
				<div className="flex flex-col items-start justify-center sm:flex-row sm:items-center sm:justify-between">
					<div className="flex flex-row items-center gap-x-3">
						<AIIcon />
						<TypographyHeader
							size="medium"
							isBold={true}
							additionalClasses="bg-gradient-to-r from-[#2828FF] to-[#6B6BFF] dark:from-[#6767FF] dark:to-[#A7A7FF] bg-clip-text text-transparent"
						>
							AI Summary Beta
						</TypographyHeader>
						<div className="flex flex-row items-center pt-1 text-textSecondaryLight dark:text-textInactiveDark">
							<Tooltip
								title=""
								tip="Bottom"
								width="w-48"
								content={
									<span>
										Beta in progress! Some information may be inaccurate—please
										verify.
									</span>
								}
							>
								<InfoIcon size="small" />
							</Tooltip>
						</div>
					</div>
					{metadata && metadata.analysis_timestamp && (
						<>
							<div>
								<TypographyLabel
									size="small"
									additionalClasses="text-textSecondaryLight dark:text-textInactiveDark"
								>
									Last updated: {formatAITimestamp(metadata.analysis_timestamp)}
								</TypographyLabel>
							</div>
						</>
					)}
				</div>

				{/* Loading state */}
				{loading && (
					<div className="flex flex-col gap-y-4 rounded-md border border-textInactiveLight bg-uiTertiaryLight p-4 dark:border-uiSecondaryDark dark:bg-grey950">
						<div className="flex flex-row items-center gap-x-1">
							<LoadingIcon />
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								Analyzing verified sources
							</TypographyParagraph>
						</div>
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							Getting the key details about this NFT from reliable sources.
						</TypographyParagraph>
						{[...Array(3)].map((_, index) => (
							<div
								key={index}
								className="flex flex-col gap-y-4 rounded-md border border-textInactiveLight bg-uiTertiaryLight p-4 dark:border-uiSecondaryDark dark:bg-grey950"
							>
								<SkeletonTheme
									baseColor={
										theme === 'light' || theme === undefined
											? '#f5f5f5'
											: '#4E4E4E'
									}
									highlightColor={
										theme === 'light' || theme === undefined
											? '#f8f8f8'
											: '#212121'
									}
								>
									<div className="w-48">
										<Skeleton count={1} className="h-6 w-full" />
									</div>
								</SkeletonTheme>
								<SkeletonTheme
									baseColor={
										theme === 'light' || theme === undefined
											? '#f5f5f5'
											: '#383838'
									}
									highlightColor={
										theme === 'light' || theme === undefined
											? '#f8f8f8'
											: '#212121'
									}
								>
									<div className="w-full">
										<Skeleton count={1} className="h-6 w-full" />
									</div>
									<div className="w-3/4">
										<Skeleton count={1} className="h-6 w-full" />
									</div>
								</SkeletonTheme>
							</div>
						))}
					</div>
				)}

				{/* Error state */}
				{error && (
					<div className="flex flex-col items-center justify-center gap-y-4 rounded-md border border-textInactiveLight bg-uiTertiaryLight p-4 dark:border-uiSecondaryDark dark:bg-grey950">
						<AlertIcon />
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							No AI Summary Available
						</TypographyParagraph>
						<div className="flex flex-col items-center">
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
							>
								We were unable to find anything that meets a quality threshold.
							</TypographyParagraph>
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
							>
								Do you have milestones you’d like to add?
							</TypographyParagraph>
						</div>
						{setSignModalOpen ? (
							<Button
								variant="secondary"
								onClick={() => setSignModalOpen(true)}
							>
								Sign
							</Button>
						) : (
							''
						)}
					</div>
				)}

				{/* Summary */}
				{metadata && !loading && (
					<>
						<div className="flex flex-col gap-y-4 rounded-md border border-textInactiveLight bg-uiTertiaryLight p-4 dark:border-uiSecondaryDark dark:bg-grey950">
							<div className="flex flex-row items-center gap-x-1 text-textPrimaryLight dark:text-textPrimaryDark">
								<AISummaryIcon />
								<TypographyCaps
									size="small"
									additionalClasses="mt-0.5 text-textPrimaryLight dark:text-textPrimaryDark"
								>
									Summary
								</TypographyCaps>
							</div>
							<TypographyParagraph
								size="medium"
								additionalClasses="dark:text-uiTertiaryLight text-uiTertiaryDark"
							>
								{metadata.enriched_data.collection_summary}
							</TypographyParagraph>
							{metadata.marketplaces && metadata.marketplaces.length && (
								<div className="flex flex-row items-center gap-x-4">
									{metadata.marketplaces.map(marketplace => (
										<div
											key={marketplace.marketplace_name}
											className="flex flex-row items-center"
										>
											<a href={marketplace.nft_url} target={'_blank'}>
												<Button
													variant="tertiary"
													rightIcon={
														<div className="text-[#4B4BFF] dark:text-[#7676FC]">
															<ArrowUpRight />
														</div>
													}
												>
													<div className="text-[#4B4BFF] dark:text-[#7676FC]">
														{marketplace.marketplace_name}
													</div>
												</Button>
											</a>
										</div>
									))}
								</div>
							)}
						</div>
						<div>{renderSignificance(metadata.enriched_data.significance)}</div>
						<div className="justify-left flex items-center">
							<button
								onClick={() =>
									openInNewTab('https://forms.gle/3mmXdG73VZXufr2f6')
								}
								className="flex h-10 items-center justify-center gap-x-1 rounded-md bg-uiTertiaryDark py-2 pl-6 pr-6 text-textPrimaryDark outline-0 ring-0 hover:bg-uiSecondaryDark hover:text-textPrimaryDark active:bg-black disabled:bg-uiTertiaryDark/50 dark:bg-light dark:text-textPrimaryLight dark:hover:bg-uiPrimaryLight dark:hover:text-textPrimaryLight dark:active:bg-uiSecondaryDark active:dark:text-white disabled:dark:bg-white/50"
							>
								<div className="justify-left inline-flex h-4 w-4 items-center">
									<svg
										width="20"
										height="20"
										viewBox="0 0 21 19"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											fillRule="evenodd"
											clipRule="evenodd"
											d="M12.382 0.0978867C12.7837 -0.0326289 13.2164 -0.0326289 13.6181 0.0978867C13.9075 0.191922 14.1291 0.351297 14.3036 0.499405C14.4625 0.634249 14.6358 0.807561 14.8134 0.985239L16.5983 2.7701C16.7768 2.94855 16.9506 3.12234 17.0858 3.28158C17.2342 3.45641 17.3938 3.67833 17.488 3.96809C17.6185 4.36984 17.6184 4.80253 17.4879 5.20416C17.3939 5.49374 17.2344 5.71552 17.0861 5.89026C16.951 6.04936 16.7774 6.22296 16.5991 6.40114L5.70711 17.2931C5.51957 17.4807 5.26522 17.586 5.00001 17.586L1.00001 17.5861C0.734788 17.5861 0.480433 17.4807 0.292895 17.2932C0.105358 17.1056 0 16.8513 0 16.5861V12.5861C0 12.3208 0.105357 12.0665 0.292892 11.8789L11.1665 1.00535L11.1858 0.986065C11.3637 0.808115 11.5371 0.634621 11.6961 0.499656C11.8707 0.35145 12.0925 0.191966 12.382 0.0978867ZM13 2.01631C12.997 2.01884 12.9938 2.02153 12.9904 2.02439C12.9015 2.09987 12.7866 2.2137 12.5775 2.42281L12.5725 2.42775L2 13.0003V15.586L4.58578 15.586L15.1707 5.00109C15.3748 4.79697 15.4868 4.68378 15.5613 4.59596C15.5643 4.5925 15.567 4.58921 15.5696 4.58608C15.567 4.58288 15.5641 4.5795 15.5611 4.57595C15.4854 4.48675 15.3712 4.37144 15.1615 4.16177L13.4217 2.42197C13.2129 2.21314 13.0982 2.09952 13.0094 2.02418C13.0061 2.02141 13.003 2.01878 13 2.01631Z"
											fill="currentColor"
										></path>
										<path
											d="M19.86 15.5C19.74 15.74 19.56 15.8407 19.32 15.9607C18.84 16.2007 18.39 16.4907 17.94 16.7607C17.59 16.9707 17.25 17.2007 16.88 17.3807C15.69 17.9407 14.71 17.5307 14.25 16.2807C14.13 15.9607 14.04 15.6307 13.9 15.3207C13.64 14.7107 13.28 14.5507 12.7 14.8907C12.15 15.2207 11.64 15.6307 11.12 16.0107C10.54 16.4407 9.98 16.9007 9.39 17.3007C7.78 18.4107 6.07 18.4607 4.31 17.6807C3.86 17.4807 3.44 17.2107 3 16.9807V16.9107C3.18 16.8707 3.35 16.8307 3.51 16.7807C4.1 16.5807 4.64 16.5607 5.25 16.8107C6.48 17.3007 7.68 17.1107 8.76 16.3407C9.51 15.8007 10.23 15.2207 10.96 14.6707C11.5 14.2607 12.03 13.8507 12.7 13.6607C13.49 13.4407 14.27 13.7107 14.69 14.4107C14.92 14.7907 15.04 15.2207 15.22 15.6307C15.32 15.8507 15.41 16.0807 15.54 16.2907C15.66 16.4907 15.87 16.5607 16.08 16.4507C16.47 16.2607 16.84 16.0607 17.22 15.8507C17.78 15.5307 18.32 15.1807 18.9 14.8907C19.37 14.6607 19.61 14.7707 19.86 15.2307C19.86 15.2307 19.8764 15.3158 19.88 15.3711C19.883 15.4177 19.86 15.5 19.86 15.5Z"
											fill="currentColor"
											stroke="currentColor"
											strokeWidth="0.75"
											strokeMiterlimit="10"
										></path>
									</svg>
								</div>
								<span className="font-abc text-labelSmallBold">
									Report Mistake
								</span>
							</button>
						</div>

						{/* Similar NFTs Section (only shows if we have them)
            Removing this for now since we don't have a design for it
          */}
						{/*{renderSimilarNFTs(metadata.similar_nfts)} */}
					</>
				)}
			</div>
		</div>
	)
}

export default LoreAI
